import { useLazyQuery, useApolloClient } from "@apollo/client";
import dayjs from "dayjs";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react"; // useState
import { useAuth } from "react-oidc-context";
import { useFeatureFlags } from "../feature-flag-react-lite";
import MaintenanceMessageContext from "./maintenance-message-context";
import { MaintenanceMessageProviderGetServerDateDocument } from "../../../generated";
import { FormatTimeCounter, IsInStorybookEnv } from "../../../constants";
import { HandleLogout } from "../handle-logout";
import { useNavigate } from "react-router-dom";
import { MaintenanceKickoutMessage } from "./maintenance-kickout-message";

export interface MaintenanceMessageConfig {}
export type MaintenanceMessageProviderProps = {
  children: ReactNode;
  storybookShowImpendingMessage?: boolean;
  storybookShowMaintenanceMessage?: boolean;
};

const MaintenanceMessageProvider: FC<MaintenanceMessageProviderProps> = (props: MaintenanceMessageProviderProps): React.JSX.Element => {
  const timeoutBeforeMaintenance = import.meta.env.VITE_TIMEOUT_BEFORE_MAINTENANCE ?? 120; //in seconds
  const [isImpending, setIsImpending] = useState<boolean | undefined>(undefined);
  const [isMaintenance, setIsMaintenance] = useState<boolean | undefined>(undefined);
  const [isApproachingMaintenance, setIsApproachingMaintenance] = useState(false);
  const [maintenanceCountdown, setMaintenanceCountdown] = useState<number>(timeoutBeforeMaintenance); //in seconds
  const auth = useAuth();
  const timerInstance = React.useRef(undefined);
  const apolloClient = useApolloClient();
  const { GetFeatureFlagByName } = useFeatureFlags();

  const [getServerDate] = useLazyQuery(MaintenanceMessageProviderGetServerDateDocument, { fetchPolicy: "network-only" });

  const impendingMaintenanceStartTimestamp = GetFeatureFlagByName("MAINTENANCE_IMPENDING_TIMESTAMP_UI_STAFF_PORTAL");
  const maintenanceStartTimestamp = GetFeatureFlagByName("MAINTENANCE_START_TIMESTAMP_UI_STAFF_PORTAL");
  const maintenanceEndTimestamp = GetFeatureFlagByName("MAINTENANCE_END_TIMESTAMP_UI_STAFF_PORTAL");
  const upcomingMaintenance = GetFeatureFlagByName("MAINTENANCE_UPCOMING_UI_STAFF_PORTAL");
  const impendingMessage = GetFeatureFlagByName("MAINTENANCE_MSG_IMPENDING_UI_STAFF_PORTAL");
  const maintenanceMessage = GetFeatureFlagByName("MAINTENANCE_MSG_SYSTEM_UI_STAFF_PORTAL");

  const navigate = useNavigate();

  useEffect(() => {
    if (maintenanceCountdown === 0 && auth.isAuthenticated) {
      setIsApproachingMaintenance(false);
      HandleLogout(auth, apolloClient, window.location.origin);
      navigate("/");
      return;
    }
    let interval: any = null;
    if (isApproachingMaintenance && auth.isAuthenticated) {
      interval = setInterval(() => {
        setMaintenanceCountdown(maintenanceCountdown - 1);
      }, 1000);
    }

    //Clearing the interval
    return () => clearInterval(interval);
  }, [maintenanceCountdown, isApproachingMaintenance, auth]);

  useEffect(() => {
    // use local feature flag when in storybook
    const isInStorybookEnv = IsInStorybookEnv();
    if (isInStorybookEnv) {
      if (props.storybookShowImpendingMessage) {
        setIsMaintenance(false);
        setIsImpending(true);
        return;
      }
      if (props.storybookShowMaintenanceMessage) {
        setIsMaintenance(true);
        setIsImpending(false);
        return;
      } else {
        setIsMaintenance(false);
        setIsImpending(false);
        return
      }
    }

    const setIntervalImmediately = async (func: any, interval: number, params: any) => {
      await func(params);
      return setInterval(func, interval, params);
    };

    const getMaintenanceMessageStatus = async (params: any) => {
      //If the GetFeatureFlag hasn't resolved it will be empty string
      if (params.upcomingMaintenance === "") return;

      if (params.upcomingMaintenance !== "true") {
        setIsMaintenance(false);
        setIsImpending(false);
        setIsApproachingMaintenance(false);
        return;
      }
      await getServerDate()
        .then((data) => {
          const serverDate = data.data?.serverDate;
          const serverTime = dayjs(serverDate);
          const impendingTime = dayjs(params.impendingMaintenanceStartTimestamp);
          const maintenanceStartTime = dayjs(params.maintenanceStartTimestamp);
          const maintenanceEndTime = dayjs(params.maintenanceEndTimestamp);

          if (serverTime > impendingTime && serverTime < maintenanceStartTime) {
            setIsMaintenance(false);
            setIsImpending(true);
            // within 1 minute before maintenance start
            if (maintenanceStartTime.diff(serverTime, "seconds") <= timeoutBeforeMaintenance) {
              setIsApproachingMaintenance(true);
              setMaintenanceCountdown(maintenanceStartTime.diff(serverTime, "seconds"));
            }
          } else if (serverTime > maintenanceStartTime && serverTime < maintenanceEndTime) {
            setIsMaintenance(true);
            setIsImpending(false);
            setIsApproachingMaintenance(false);
            return;
          } else {
            setIsMaintenance(false);
            setIsImpending(false);
            setIsApproachingMaintenance(false);
            return;
          }
        })
        .catch((exception: any) => {
          console.log("exception: ", exception);
        });
    };

    const intervalParams = {
      maintenanceStartTimestamp: maintenanceStartTimestamp,
      maintenanceEndTimestamp: maintenanceEndTimestamp,
      impendingMaintenanceStartTimestamp: impendingMaintenanceStartTimestamp,
      upcomingMaintenance: upcomingMaintenance,
    };

    if (timerInstance.current) {
      //cancel timer
      clearInterval(timerInstance.current);
    }
    setIntervalImmediately(getMaintenanceMessageStatus, 5000, intervalParams)
      .then((interval: any) => {
        timerInstance.current = interval;
      })
      .catch((exception: any) => {
        console.log(exception);
      });
  }, [impendingMaintenanceStartTimestamp, maintenanceEndTimestamp, maintenanceStartTimestamp, upcomingMaintenance]);

  const contextValues = useMemo(
    () => ({
      isImpending: isImpending,
      isMaintenance: isMaintenance,
      impendingMessage: impendingMessage,
      maintenanceMessage: maintenanceMessage,
      impendingMaintenanceStartTimestamp: impendingMaintenanceStartTimestamp,
      maintenanceStartTimestamp: maintenanceStartTimestamp,
      maintenanceEndTimestamp: maintenanceEndTimestamp,
      isApproachingMaintenance: isApproachingMaintenance,
    }),
    [isImpending, isMaintenance, isApproachingMaintenance]
  );

  return (
    <MaintenanceMessageContext.Provider value={contextValues}>
      {isApproachingMaintenance && auth.isAuthenticated && <MaintenanceKickoutMessage timer={FormatTimeCounter(maintenanceCountdown)} />}
      {props.children}
    </MaintenanceMessageContext.Provider>
  );
};

export default MaintenanceMessageProvider;
